/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogCategoryFragment, CosmosEnumButtonVariants } from "@cosmos/hygraph-schema";

import { CosmosTagProps } from "@components/elements/atoms";
import { BlogCardProps, ContentLargeImageSectionProps } from "@components/elements/ref-sections";
import { isAbsolute, pathnameHasLocale, stripLocalePrefix } from "@cosmos/common-utils";
import { GetLocalizedPathFn, defaultGetLocalizedPath } from "@components/context";

import { FeedItem } from "./feed-card.types";

const getAllBlogCategories = (item: FeedItem, blogCategories: BlogCategoryFragment[]): BlogCategoryFragment[] => {
	if (!item.categories || !item.categories.length) return [];

	return item.categories
		.map((categoryId) => blogCategories.find((blogCat) => blogCat.categoryId === categoryId))
		.filter((category): category is BlogCategoryFragment => category !== undefined);
};

const isFeedUrlAbs = (item: FeedItem): boolean => {
	const href = item.url || "";
	const isAbs = isAbsolute(href);
	if (isAbs) {
		return true;
	}
	return false;
};

export const getFeedCardHref = (
	locale: string,
	locales: string[],
	item: FeedItem,
	currentBrand?: string,
	getLocalizedPath?: GetLocalizedPathFn,
): string => {
	if (!item.url) return "";

	if (isFeedUrlAbs(item)) {
		return item.url;
	}

	let relativeHref = item.url;
	//ensure url has a leading slash
	if (item.url.charAt(0) !== "/") {
		relativeHref = `/${relativeHref}`;
	}

	const queryParamMode = process.env.NEXT_PUBLIC_LOCALE_MODE === "never";

	if (queryParamMode) {
		// In "never" mode, strip any existing locale prefix — URLs must be clean.
		relativeHref = stripLocalePrefix(locales, relativeHref);
	} else if (!pathnameHasLocale(locales, relativeHref)) {
		// In "always" or "as-needed" mode, add locale prefix if missing.
		const pathFn = getLocalizedPath ?? defaultGetLocalizedPath;
		relativeHref = pathFn(relativeHref, locale);
	}

	if (currentBrand && item.brand !== currentBrand && item.originUrl) {
		//make href absolute
		return `${item.originUrl}${relativeHref}`;
	}
	return relativeHref;
};

export const getContentLargeImageFragment = (
	locale: string,
	locales: string[],
	item: FeedItem,
	blogCategories: BlogCategoryFragment[],
	pinnedBadge: CosmosTagProps,
	readMoreLabel: string,
	rootSlug: string,
	currentBrand?: string,
	getLocalizedPath?: GetLocalizedPathFn,
	hideCategoryPills?: boolean,
): ContentLargeImageSectionProps => {
	const badges: CosmosTagProps[] = [
		{
			label: item.sticky ? pinnedBadge.label : undefined,
			icon: item.sticky ? pinnedBadge.icon : undefined,
		},
	];
	if (!hideCategoryPills) {
		const allCategories = getAllBlogCategories(item, blogCategories);
		allCategories.forEach((category) => {
			const pathFn = getLocalizedPath ?? defaultGetLocalizedPath;
			const categoryHref = category.tag?.tagId ? `${pathFn(rootSlug, locale)}?tags=${category.tag.tagId}` : "";
			badges.push({
				label: category.label || "",
				href: categoryHref,
				backgroundColor: category.badgeColor || "",
				color: category.badgeColor ? "black" : "",
			});
		});
	}

	return {
		__typename: "CosmosSectionContentLargeImage",
		id: "",
		badges,
		date: item.activeDate,
		title: {
			text: item.title || "",
			html: item.title || "",
		},
		desc: item.description || "",
		asset: {
			id: "",
			url: item?.image?.url || "",
			//empty string to get replaced by hygraphImageLoader
			optUrl: "",
			width: 1200,
			altText: item?.image?.text,
		},
		blurredImage: {
			id: "",
			url: item?.image?.url || "",
			//empty string to get replaced by hygraphImageLoader
			optUrl: "",
			width: 100,
		},
		buttons: [
			{
				label: readMoreLabel || "Read more",
				href: getFeedCardHref(locale, locales, item, currentBrand, getLocalizedPath),
				variant: CosmosEnumButtonVariants.Primary,
			},
		],
	};
};

export const getBlogCardFragment = (
	locale: string,
	locales: string[],
	item: FeedItem,
	blogCategories: BlogCategoryFragment[],
	pinnedBadge: CosmosTagProps,
	rootSlug: string,
	currentBrand?: string,
	getLocalizedPath?: GetLocalizedPathFn,
	hideCategoryPills?: boolean,
): BlogCardProps => {
	const badges: CosmosTagProps[] = [
		{
			label: item.sticky ? pinnedBadge.label : undefined,
			icon: item.sticky ? pinnedBadge.icon : undefined,
		},
	];
	if (!hideCategoryPills) {
		const allCategories = getAllBlogCategories(item, blogCategories);
		allCategories.forEach((category) => {
			const pathFn = getLocalizedPath ?? defaultGetLocalizedPath;
			const categoryHref = category.tag?.tagId ? `${pathFn(rootSlug, locale)}?tags=${category.tag.tagId}` : "";
			badges.push({
				label: category.label || "",
				href: categoryHref,
				backgroundColor: category.badgeColor || "",
				color: category.badgeColor ? "black" : "",
			});
		});
	}

	return {
		badges,
		date: item.activeDate,
		title: {
			text: "",
			html: item.title || "",
		},
		desc: item.description || "",
		href: getFeedCardHref(locale, locales, item, currentBrand, getLocalizedPath),
		asset: {
			id: "",
			url: item?.image?.url || "",
			//empty string to get replaced by hygraphImageLoader
			optUrl: "",
			width: 900,
			altText: item?.image?.text,
		},
		likeCount: item.likeCount,
		replyCount: item.replyCount,
		author: item.author,
		buttons: [],
	};
};
